import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

/**
 * Hook for product search, browsing, and category filtering.
 *
 * Fetches products from the SymplePOS REST API with debounced search,
 * category filtering, and pagination support. Categories are fetched
 * once on mount.
 *
 * @param {Object}  options              Initial options.
 * @param {string}  options.initialSearch   Initial search term (default '').
 * @param {string}  options.initialCategory Initial category filter (default '').
 * @param {number}  options.perPage         Results per page (default 20).
 * @param {boolean} options.enabled         Whether to fetch products (default true).
 * @returns {Object} Product state and controls.
 */
export default function useProducts({
  initialSearch = '',
  initialCategory = '',
  perPage = 20,
  enabled = true,
} = {}) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);

  // Track the latest search/category values for debounced fetching.
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState(initialCategory);

  // Ref to track the latest request so we can discard stale responses.
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);

  /**
   * Core fetch — calls the API and updates state.
   * Ignores responses from superseded requests.
   */
  const executeFetch = useCallback(
    async (searchTerm, categoryId, pageNum) => {
      const currentRequestId = ++requestIdRef.current;

      setLoading(true);
      setError(null);

      try {
        const response = await api.getProducts({
          search: searchTerm,
          category: categoryId,
          page: pageNum,
          per_page: perPage,
        });

        // Only apply the result if this is still the most recent request.
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        // The REST endpoint may return the list directly or wrapped in an
        // object with pagination metadata. Handle both shapes defensively.
        if (Array.isArray(response)) {
          setProducts(response);
          setTotal(response.length);
          setTotalPages(1);
        } else {
          setProducts(response.products ?? response.data ?? []);
          setTotal(response.total ?? 0);
          setTotalPages(response.total_pages ?? response.totalPages ?? 0);
        }
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        setError(err?.message || 'Failed to load products.');
        setProducts([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        // Only clear loading if this is still the active request.
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [perPage],
  );

  /**
   * Public API — allows imperatively triggering a fetch.
   * Resets to page 1 when search or category change.
   */
  const fetchProducts = useCallback(
    (newSearch, newCategory, newPage) => {
      const s = newSearch ?? search;
      const c = newCategory ?? category;
      const p = newPage ?? 1;

      setSearch(s);
      setCategory(c);
      setPage(p);
      executeFetch(s, c, p);
    },
    [search, category, executeFetch],
  );

  // ---------------------------------------------------------------
  // Debounced auto-fetch when search, category, or page changes
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;

    // Clear any pending debounce timer.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If the search term is being typed, debounce 300ms.
    // Category and page changes fire immediately.
    const delay = search !== '' ? 300 : 0;

    debounceTimerRef.current = setTimeout(() => {
      executeFetch(search, category, page);
    }, delay);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [enabled, search, category, page, executeFetch]);

  // ---------------------------------------------------------------
  // Fetch categories once when enabled
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    api
      .getCategories()
      .then((result) => {
        if (!cancelled) {
          setCategories(Array.isArray(result) ? result : result.data ?? []);
        }
      })
      .catch(() => {
        // Categories are non-critical; silently ignore errors.
        if (!cancelled) {
          setCategories([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // ---------------------------------------------------------------
  // Reset to page 1 whenever search or category changes
  // ---------------------------------------------------------------
  const prevSearchRef = useRef(search);
  const prevCategoryRef = useRef(category);

  useEffect(() => {
    if (
      prevSearchRef.current !== search ||
      prevCategoryRef.current !== category
    ) {
      prevSearchRef.current = search;
      prevCategoryRef.current = category;
      setPage(1);
    }
  }, [search, category]);

  /**
   * Patch a single product in the local list (e.g. after a stock update).
   */
  const patchProduct = useCallback((productId, updates) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, ...updates } : p))
    );
  }, []);

  return {
    products,
    categories,
    loading,
    error,
    total,
    totalPages,
    page,
    setPage,
    search,
    setSearch,
    category,
    setCategory,
    fetchProducts,
    patchProduct,
  };
}
